import { Heading, DescriptionList, Chip } from '@circlefin/components'
import { useDate, useMoney } from '@circlefin/hooks'
import { HistoryBackButton } from '@containers/layout'
import { currencyToToken } from '@shared/components/common'
import { GraphQLErrorBoundary } from '@shared/components/errors'
import { Content } from '@shared/components/layout'
import { FeeStatus, FeeType, useFeeReportQuery } from '@shared/graphql'
import useTranslation from 'next-translate/useTranslation'

import { RelatedTransactionsTable } from '../RelatedTransactionsTable/RelatedTransactionsTable'

import type { ChipProps } from '@circlefin/components/lib/Chip'

const chipStatusMap: Record<FeeStatus, ChipProps['variant']> = {
  [FeeStatus.paid]: 'default/success',
  [FeeStatus.pending]: 'default/info',
  [FeeStatus.scheduled]: 'default/neutral',
}

export interface FeeDetailsProps {
  /**
   * Fee report id.
   */
  feeReportId: string
}

export const FeeDetails: React.FC<FeeDetailsProps> = ({ feeReportId }) => {
  const { t } = useTranslation('settings')
  const { date: formatDate } = useDate()
  const { money } = useMoney()

  const { data, loading, error } = useFeeReportQuery({
    variables: {
      id: feeReportId,
    },
  })

  const feeReport = data?.feeReport
  if (loading) return null

  return (
    <Content>
      <HistoryBackButton />
      <Heading variant="page/sm">
        <Heading.Eyebrow>{t('billing.feeDetails.title')}</Heading.Eyebrow>
        <Heading.Header>
          {feeReport &&
            money({
              number: feeReport.fee.amount,
              variant: currencyToToken(feeReport.fee.currency),
              options: {
                symbol: true,
                signDisplay: false,
              },
            })}
        </Heading.Header>
      </Heading>

      <GraphQLErrorBoundary error={error}>
        <div className="flex flex-col gap-2">
          <h2 className="type-h-title-lg">
            {t('billing.feeDetails.details.title')}
          </h2>

          {feeReport && (
            <div className="flex flex-col gap-4">
              <DescriptionList density="loose">
                <DescriptionList.Label className="w-64">
                  {t('billing.feeDetails.details.status')}
                </DescriptionList.Label>
                <DescriptionList.Description className="flex items-center gap-4">
                  <Chip variant={chipStatusMap[feeReport.status]}>
                    {t(
                      `billing.feeDetails.details.statusLabels.${feeReport.status}`,
                    )}
                  </Chip>
                  <p className="type-body-base">
                    {formatDate(feeReport.updateDate, 'monthDayYear')}
                  </p>
                </DescriptionList.Description>

                <DescriptionList.Label className="w-64">
                  {t('billing.feeDetails.details.amount')}
                </DescriptionList.Label>
                <DescriptionList.Description>
                  <p className="type-body-base-bold">
                    {money({
                      number: feeReport.fee.amount,
                      variant: currencyToToken(feeReport.fee.currency),
                      options: {
                        symbol: true,
                        signDisplay: false,
                      },
                    })}
                  </p>
                </DescriptionList.Description>

                <DescriptionList.Label className="w-64">
                  {t('billing.feeDetails.details.date')}
                </DescriptionList.Label>
                <DescriptionList.Description>
                  <p className="type-body-base">
                    {formatDate(feeReport.createDate, 'monthDayYear')}
                  </p>
                </DescriptionList.Description>

                <DescriptionList.Label className="w-64">
                  {t('billing.feeDetails.details.feeType')}
                </DescriptionList.Label>
                <DescriptionList.Description>
                  <p className="type-body-base">
                    {t(
                      `billing.feeDetails.details.feeTypeLabels.${feeReport.feeType}`,
                    )}
                  </p>
                </DescriptionList.Description>

                <DescriptionList.Label className="w-64">
                  {t('billing.feeDetails.details.redemptionAmount')}
                </DescriptionList.Label>
                <DescriptionList.Description>
                  <p className="type-body-base-bold">
                    {money({
                      number: feeReport.cumulatedPayoutAmount.amount,
                      variant: currencyToToken(
                        feeReport.cumulatedPayoutAmount.currency,
                      ),
                      options: {
                        symbol: true,
                        signDisplay: false,
                      },
                    })}
                  </p>
                </DescriptionList.Description>
                {feeReport.feeType === FeeType.net && (
                  <>
                    <DescriptionList.Label className="w-64">
                      {t('billing.feeDetails.details.netRedemptionAmount')}
                    </DescriptionList.Label>
                    <DescriptionList.Description>
                      <p className="type-body-base-bold">
                        {money({
                          number: feeReport.cumulatedNetAmount.amount,
                          variant: currencyToToken(
                            feeReport.cumulatedNetAmount.currency,
                          ),
                          options: {
                            symbol: true,
                            signDisplay: false,
                          },
                        })}
                      </p>
                    </DescriptionList.Description>
                  </>
                )}
              </DescriptionList>

              <RelatedTransactionsTable feeReport={feeReport} />
            </div>
          )}
        </div>
      </GraphQLErrorBoundary>
    </Content>
  )
}
